// The sales pipeline: prospects grouped by STAGE, so movement is visible at a glance
// — where deals are stuck and where they're progressing. Each lead is a compact,
// collapsible card (LeadRow); its next-step tasks live inside it. The all/mine filter
// is by lead OWNERSHIP (plus any lead with an open task assigned to me, so a handed-
// off errand on someone else's lead never becomes invisible).
//
// Three more filters sit on top, all client-side over the leads already loaded:
// STAGE (the pipeline rail doubles as the control), URGENCY (any lead holding an
// open task at that level), and a free-text SEARCH over name, contact and phone.
//
// ORDER is a separate matter from filtering, and it has two layers. The sort strip
// (LeadSortBar) picks the column, shared with the clients table through useTableSort
// so both screens order text by the same rules. Above it sits one fixed rule: a lead
// with an OPEN URGENT task floats to the top — of its own stage section, not of the
// whole screen. Lifting it clear of the grouping would cost the thing this screen
// exists to show, since a lead's position is what tells you which stage it is in, and
// the rail's counts would stop matching the sections beneath them. Seeing only what
// burns, across every stage at once, is what the urgency filter already does.
import { useState } from 'react';
import { Link } from 'react-router-dom';
import useAsync from '../../hooks/useAsync';
import useTableSort from '../../hooks/useTableSort';
import useStableOrder from '../../hooks/useStableOrder';
import { tasksApi, clientsApi } from '../../api';
import he from '../../i18n/he';
import PageHeader from '../../components/PageHeader';
import AsyncBoundary, { ErrorText } from '../../components/Loader';
import EmptyState from '../../components/EmptyState';
import Button from '../../components/Button';
import TaskModal from '../../components/TaskModal';
import ListPanel from '../../components/lists/ListPanel';
import UrgencyFilter from '../../components/UrgencyFilter';
import SearchBox from '../../components/SearchBox';
import { matchesUrgency } from '../../components/UrgencyBadge';
import { DoneUndoProvider } from '../../components/DoneUndo';
import LeadRow from './LeadRow';
import LeadModal from './LeadModal';
import ConvertModal from './ConvertModal';
import MarkLostModal from './MarkLostModal';
import PipelineRail from './PipelineRail';
import LeadSortBar from './LeadSortBar';
import { SORT_COLUMNS, urgentFirst } from './leadOrder';
import SelectionBar from './SelectionBar';
import DeleteLeadsModal from './DeleteLeadsModal';

const STAGE_ORDER = ['new', 'contacted', 'meeting', 'proposal', 'negotiation'];

export default function Leads() {
  const [mine, setMine] = useState(false);
  // The payload says which view it answers, so the order resets on the data for a view
  // and never on rows still on screen from the previous one (useStableOrder).
  const { data: payload, loading, refreshing, error, reload } = useAsync(
    () => tasksApi.board(mine, 'leads').then((groups) => ({ view: String(mine), groups })),
    [mine],
  );
  const data = payload && payload.groups;
  const [listFor, setListFor] = useState(null); // a task whose new list is open
  const [editing, setEditing] = useState(null); // task modal: task, {related_client_id}, or null
  const [editingLead, setEditingLead] = useState(null); // lead object, {} for new, or null
  const [leadLoadError, setLeadLoadError] = useState(null);
  // THE FORM EDITS THE FULL CLIENT, read fresh. The board carries a slim copy — the main
  // contact only, and no owner, source, follow-up or notes at the top level — and a form
  // seeded from it saved those as blanks. Opening from the full record keeps every other
  // contact (071) and every field the board does not carry.
  const openLead = async (c) => {
    setLeadLoadError(null);
    try {
      setEditingLead(await clientsApi.get(c.id));
    } catch (e) {
      setLeadLoadError(e);
    }
  };
  // The two terminal stage moves, each confirmed in its own dialog before it happens.
  const [converting, setConverting] = useState(null); // lead being turned into a customer
  const [losing, setLosing] = useState(null); // lead being marked lost
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [stage, setStage] = useState(null); // null = every stage
  const [urgency, setUrgency] = useState(null); // null = every urgency
  const [search, setSearch] = useState(''); // free text over name / contact / phone
  const [picked, setPicked] = useState(() => new Set()); // ticked lead ids
  const [deleting, setDeleting] = useState(false); // the bulk-delete dialog is open
  const startNote = (id) => setEditingNoteId(id);
  const stopNote = (id) => setEditingNoteId((cur) => (cur === id ? null : cur));

  // Urgency and search filter first: a lead qualifies on urgency when it holds an OPEN
  // task at that level, and on search when the text appears in what the card shows of
  // its identity. The rail's counts are taken AFTER both but BEFORE the stage filter,
  // so the stage you are filtering by still shows every other stage to switch to — and
  // a search says which stages its matches are in rather than emptying the rail.
  const term = search.trim().toLowerCase();
  const matchesSearch = (g) => !term
    || [g.client.name, g.client.contact_name, g.client.phone, g.client.contacts_text]
      .some((v) => v != null && String(v).toLowerCase().includes(term));
  const byFilters = (data || [])
    .filter((g) => matchesUrgency(g.tasks, urgency))
    .filter(matchesSearch);

  // Sorted ONCE, across every stage, then split into sections. Splitting a sorted list
  // preserves the order within each part, so a single pass orders all five sections
  // identically — and it keeps the hook call count fixed, which sorting per section
  // could not.
  //
  // No initial sort, deliberately unlike the clients table: the order the server sends
  // is overdue-next-steps first and then by name, a composite no single column states,
  // so seeding the strip with a name arrow would claim an order the list is not in.
  // The arrow appears when a partner picks a column.
  const { sort, toggle, rows: sorted } = useTableSort(SORT_COLUMNS, byFilters);
  // THE SCREEN STAYS WHERE IT WAS after a save: the order above is taken once per view
  // (opening, or any filter or sort change) and kept through every reload, so marking a
  // lead's task done never moves the card being read. See hooks/useStableOrder.
  const ranked = STAGE_ORDER.flatMap((s) => urgentFirst(sorted.filter((g) => g.lead.stage === s)));
  const { ordered, keep, forget } = useStableOrder(data ? ranked : null, {
    keyOf: (g) => g.client.id,
    resetKey: payload && [payload.view, urgency, term, JSON.stringify(sort)].join('|'),
  });
  const allStages = STAGE_ORDER
    .map((s) => ({ stage: s, items: (ordered || []).filter((g) => g.lead.stage === s) }));
  const counts = Object.fromEntries(allStages.map((sec) => [sec.stage, sec.items.length]));
  // Empty stages stay out of the list (nothing to show) but are still counted in
  // the rail, where a zero is the point.
  const sections = allStages.filter((sec) => sec.items.length > 0 && (!stage || sec.stage === stage));

  // Selection is over the leads CURRENTLY ON SCREEN, and it is derived rather than
  // stored that way: a tick survives a filter change (tick, narrow, widen again, still
  // ticked) but a lead the filters have hidden can never be part of a delete, because
  // the id list handed to the dialog is filtered through what is visible right now.
  const visibleIds = sections.flatMap((sec) => sec.items.map((g) => g.client.id));
  const selectedIds = visibleIds.filter((id) => picked.has(id));
  const toggleOne = (id) => setPicked((cur) => {
    const next = new Set(cur);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  // All on, unless they already all are — then off. Ids outside the visible set are
  // left alone, so selecting all within one stage does not silently drop another's.
  const toggleAll = () => setPicked((cur) => {
    const next = new Set(cur);
    const all = visibleIds.length > 0 && visibleIds.every((id) => next.has(id));
    for (const id of visibleIds) { if (all) next.delete(id); else next.add(id); }
    return next;
  });
  const clearPicked = () => setPicked(new Set());

  const rowProps = (g) => ({
    group: g,
    onChanged: reload,
    selected: picked.has(g.client.id),
    onToggleSelect: () => toggleOne(g.client.id),
    onEdit: (t) => setEditing(t),
    onAdd: (c) => setEditing({ related_client_id: c.id }),
    onEditLead: openLead,
    onConvert: (c) => setConverting(c),
    onMarkLost: (c) => setLosing(c),
    editingNoteId,
    onStartNote: startNote,
    onStopNote: stopNote,
  });

  return (
    <DoneUndoProvider onDone={(t) => keep(t.related_client_id)} onUndone={reload}>
      <PageHeader title={he.leads.title} spread>
        <div className="row">
          <div className="seg">
            <Button small variant={mine ? 'subtle' : ''} onClick={() => setMine(false)}>{he.leads.allLeads}</Button>
            <Button small variant={mine ? '' : 'subtle'} onClick={() => setMine(true)}>{he.leads.mineLeads}</Button>
          </div>
          <SearchBox value={search} onChange={setSearch} placeholder={he.clients.search} />
        </div>
        <div className="seg">
          <Button small onClick={() => setEditingLead({})}>{he.leads.newLead}</Button>
          <Link to="/leads/import"><Button small variant="subtle">{he.leads.import}</Button></Link>
        </div>
      </PageHeader>
      {leadLoadError && <ErrorText error={leadLoadError} />}

      <AsyncBoundary loading={loading} error={error}>
        {data && (data.length === 0
          ? <EmptyState text={he.empty.leads} />
          : (
            <>
              <UrgencyFilter value={urgency} onChange={setUrgency} />

              <PipelineRail stages={STAGE_ORDER} counts={counts} active={stage} onSelect={setStage} />

              {visibleIds.length > 0 && (
                <SelectionBar
                  total={visibleIds.length}
                  selectedCount={selectedIds.length}
                  onToggleAll={toggleAll}
                  onClear={clearPicked}
                  onDelete={() => setDeleting(true)}
                />
              )}

              {sections.length === 0
                ? <EmptyState text={he.leads.noneMatch} />
                : (
                  // Dimmed while reloading. Crucially the rows stay mounted, so an
                  // expanded lead card no longer collapses when a note or a stage
                  // change saves inside it.
                  <div className={`lead-list${refreshing ? ' refreshing' : ''}`}>
                    <LeadSortBar columns={SORT_COLUMNS} sort={sort} onSort={toggle} />
                    {sections.map((sec) => (
                      <section key={sec.stage} className="lead-stage" data-stage={sec.stage}>
                        <h2 className="lead-stage-title">
                          {he.leadStages[sec.stage]}<span className="count">{sec.items.length}</span>
                        </h2>
                        {sec.items.map((g) => <LeadRow key={g.client.id} {...rowProps(g)} />)}
                      </section>
                    ))}
                  </div>
                )}
            </>
          ))}
      </AsyncBoundary>

      {editing && (
        <TaskModal task={editing} onClose={() => setEditing(null)} onSaved={(r) => { setEditing(null); reload(); if (r?.openListFor) setListFor(r.openListFor); }} />
      )}
      {/* A list made with its task opens at once, ready for the first name. */}
      {listFor && <ListPanel taskId={listFor} onClose={() => setListFor(null)} onChanged={reload} />}
      {editingLead && (
        <LeadModal lead={editingLead} onClose={() => setEditingLead(null)} onSaved={() => { setEditingLead(null); reload(); }} />
      )}
      {/* Converting reloads but stays open: its last state is the message explaining
          where the lead just went, which would be lost if the dialog closed itself. */}
      {converting && (
        <ConvertModal lead={converting} onClose={() => setConverting(null)} onConverted={() => { forget(converting.id); reload(); }} />
      )}
      {losing && (
        <MarkLostModal lead={losing} onClose={() => setLosing(null)} onDone={() => { forget(losing.id); setLosing(null); reload(); }} />
      )}
      {/* Opened on the ids visible AND ticked at that moment — the same list the delete
          is sent, so the count the dialog names is the count that goes. The selection is
          cleared only on success; a refused delete leaves it ticked to be retried. */}
      {deleting && selectedIds.length > 0 && (
        <DeleteLeadsModal
          ids={selectedIds}
          onClose={() => setDeleting(false)}
          onDeleted={() => { selectedIds.forEach(forget); setDeleting(false); clearPicked(); reload(); }}
        />
      )}
    </DoneUndoProvider>
  );
}
